import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Tabs,
  Tab,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import {
  createOperationsManagementRelationship,
  fetchOperationsManagementRelationships,
  fetchOperationsManagementSettings,
  fetchOperationsManagementTeams,
  saveOperationsManagementSettings,
  testWebsiteLeadEmail,
  updateOperationsManagementRelationship,
  updateOperationsManagementRelationshipStatus,
} from "../../../../services/operationsManagement";
import {
  buildOperationsManagementPayload,
  EMPTY_OPERATIONS_MANAGEMENT_FORM,
  getInitialOperationsManagementFormValues,
  getReviewerOptionsForManagingTeam,
  normalizeId,
  reconcileReviewerUserIdForManagingTeam,
} from "./operationsManagementForm";

const WORKFLOW_KEY_CLIENT_TO_CUSTOMER_REVIEW = "client-to-customer-review";
const WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW = "customer-terms-review";
const DEFAULT_TERMS_REVIEW_FALLBACK_EMAIL = "info@elitedm.ca";
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim().toLowerCase());

const getWorkflowDisplayLabel = (workflowKey) =>
  workflowKey === WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW
    ? "Customer Terms Review"
    : "Client-to-Customer Review";

const isActiveWorkflowAssignment = (relationship = {}) =>
  relationship?.isActive === true &&
  Boolean(normalizeId(relationship?.reviewerUserId || relationship?.reviewer?.userId)) &&
  Boolean(normalizeId(relationship?.appliesToTeamId || relationship?.managedTeamId || relationship?.appliesToTeam?.id || relationship?.managedTeam?.id)) &&
  Boolean(normalizeId(relationship?.managingTeamId || relationship?.managingTeam?.id));

const formatReviewerLabel = (reviewer) => {
  const name =
    reviewer?.name ||
    [reviewer?.firstName, reviewer?.lastName].filter(Boolean).join(" ").trim();
  const email = reviewer?.email ? `<${reviewer.email}>` : "";
  const role = reviewer?.role ? `(${reviewer.role})` : "";
  return [name || `User ${reviewer?.userId}`, email, role].filter(Boolean).join(" ");
};

const OperationsManagementSettingsPanel = ({ instanceUniqueId, canManage = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [relationships, setRelationships] = useState([]);
  const [teams, setTeams] = useState([]);
  const [reviewerOptions, setReviewerOptions] = useState([]);
  const [workflowLabel, setWorkflowLabel] = useState("Client-to-Customer Review");
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [settings, setSettings] = useState({
    customerReviewEnabled: true,
    termsReviewEnabled: false,
    requireCustomerReviewBeforeTermsApproval: false,
    termsReviewFallbackEmail: DEFAULT_TERMS_REVIEW_FALLBACK_EMAIL,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [operationsTab, setOperationsTab] = useState(0);
  const [websiteRecipients, setWebsiteRecipients] = useState("info@elitedm.ca");
  const [websiteAssigneeIds, setWebsiteAssigneeIds] = useState([]);
  const [websiteNotifyOwners, setWebsiteNotifyOwners] = useState(true);
  const [websiteSaved, setWebsiteSaved] = useState(false);
  const [leadTestStatus, setLeadTestStatus] = useState("");
  const [sendingLeadTest, setSendingLeadTest] = useState(false);
  const [canManageFoundation, setCanManageFoundation] = useState(false);
  const [fallbackEmailInput, setFallbackEmailInput] = useState(
    DEFAULT_TERMS_REVIEW_FALLBACK_EMAIL
  );
  const [fallbackEmailError, setFallbackEmailError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSubmitting, setDialogSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [formValues, setFormValues] = useState(EMPTY_OPERATIONS_MANAGEMENT_FORM);
  const [addOtherWorkflow, setAddOtherWorkflow] = useState(false);
  const [otherCanApprove, setOtherCanApprove] = useState(false);
  const [primaryAssignmentSaved, setPrimaryAssignmentSaved] = useState(false);

  const loadData = useCallback(async () => {
    if (!instanceUniqueId) return;

    setLoading(true);
    setError("");
    try {
      const [relationshipsRes, teamsRes, settingsRes] = await Promise.all([
        fetchOperationsManagementRelationships(instanceUniqueId),
        fetchOperationsManagementTeams(instanceUniqueId),
        fetchOperationsManagementSettings(instanceUniqueId),
      ]);

      setRelationships(
        Array.isArray(relationshipsRes?.relationships) ? relationshipsRes.relationships : []
      );
      setTeams(Array.isArray(teamsRes?.teams) ? teamsRes.teams : []);
      setReviewerOptions(
        Array.isArray(teamsRes?.reviewerOptions) ? teamsRes.reviewerOptions : []
      );
      setWorkflowLabel(teamsRes?.workflowLabel || "Client-to-Customer Review");
      setWorkflowOptions(
        Array.isArray(teamsRes?.workflowOptions) ? teamsRes.workflowOptions : []
      );
      setCanManageFoundation(settingsRes?.permissions?.canManageFoundationalSettings === true);
      const operationsSettings = Object.fromEntries(
        Object.entries(settingsRes?.settings || {}).filter(([key]) => key !== "production")
      );
      setSettings((prev) => ({ ...prev, ...operationsSettings }));
      setWebsiteRecipients((operationsSettings.websiteLeadRecipients || ["info@elitedm.ca"]).join(", "));
      setWebsiteAssigneeIds(
        (operationsSettings.websiteLeadAssigneeUserIds || [])
          .map((value) => Number(value))
          .filter((value) => Number.isInteger(value) && value > 0)
      );
      setWebsiteNotifyOwners(operationsSettings.websiteLeadNotifyOwners !== false);
      setWebsiteSaved(true);
    } catch (loadError) {
      setCanManageFoundation(false);
      setError(loadError?.response?.data?.error || "Unable to load operations management settings.");
    } finally {
      setLoading(false);
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setFallbackEmailInput(
      settings.termsReviewFallbackEmail || DEFAULT_TERMS_REVIEW_FALLBACK_EMAIL
    );
  }, [settings.termsReviewFallbackEmail]);

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((left, right) =>
        String(left?.name || "").localeCompare(String(right?.name || ""))
      ),
    [teams]
  );

  const websiteLeadAssigneeOptions = useMemo(
    () =>
      (Array.isArray(reviewerOptions) ? reviewerOptions : []).filter((user) =>
        !user?.archived &&
        ["client-rep", "order-rep"].includes(
          String(user?.normalizedRole || user?.role || "").trim().toLowerCase()
        )
      ),
    [reviewerOptions]
  );

  const openCreateDialog = (workflowKey = WORKFLOW_KEY_CLIENT_TO_CUSTOMER_REVIEW) => {
    setDialogError("");
    setAddOtherWorkflow(false);
    setOtherCanApprove(false);
    setPrimaryAssignmentSaved(false);
    setFormValues({
      ...EMPTY_OPERATIONS_MANAGEMENT_FORM,
      workflowKey,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (relationship) => {
    setDialogError("");
    setAddOtherWorkflow(false);
    setOtherCanApprove(false);
    setPrimaryAssignmentSaved(false);
    setFormValues(getInitialOperationsManagementFormValues(relationship));
    setDialogOpen(true);
  };

  const managingTeamReviewerOptions = useMemo(
    () =>
      getReviewerOptionsForManagingTeam({
        managingTeamId: formValues.managingTeamId,
        teams,
        reviewerOptions,
      }),
    [formValues.managingTeamId, teams, reviewerOptions]
  );

  const termsReviewAssignments = useMemo(
    () =>
      relationships.filter(
        (relationship) =>
          relationship?.workflowKey === WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW
      ),
    [relationships]
  );

  const activeTermsReviewAssignments = useMemo(
    () => termsReviewAssignments.filter(isActiveWorkflowAssignment),
    [termsReviewAssignments]
  );

  const hasConfiguredTermsReviewAssignment = activeTermsReviewAssignments.length > 0;
  const fallbackEmail =
    settings.termsReviewFallbackEmail || DEFAULT_TERMS_REVIEW_FALLBACK_EMAIL;
  const termsReviewStatusText = settings.termsReviewEnabled
    ? hasConfiguredTermsReviewAssignment
      ? `Terms Review is active. ${activeTermsReviewAssignments.length} reviewer assignment${activeTermsReviewAssignments.length === 1 ? "" : "s"} are configured.`
      : `Terms Review is enabled, but no active reviewer assignments exist. Requests will be sent to ${fallbackEmail} for manual review.`
    : "Terms Review is currently disabled.";

  const dialogWorkflowLabel = getWorkflowDisplayLabel(formValues.workflowKey);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogSubmitting(false);
    setDialogError("");
    setPrimaryAssignmentSaved(false);
    setFormValues(EMPTY_OPERATIONS_MANAGEMENT_FORM);
  };

  const handleSave = async () => {
    setDialogSubmitting(true);
    setDialogError("");

    const payload = buildOperationsManagementPayload({
      formValues,
      instanceUniqueId,
    });

    let savedPrimary = primaryAssignmentSaved;
    try {
      if (!savedPrimary) {
        if (formValues.id) {
          await updateOperationsManagementRelationship(formValues.id, payload);
        } else {
          await createOperationsManagementRelationship(payload);
        }
        // A retry after a secondary-workflow error must never recreate the primary.
        savedPrimary = true;
        setPrimaryAssignmentSaved(true);
      }
      if (addOtherWorkflow) {
        const otherKey = formValues.workflowKey === WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW
          ? WORKFLOW_KEY_CLIENT_TO_CUSTOMER_REVIEW : WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW;
        // Check fresh server state to avoid duplicate secondary records after a retry
        // or a concurrent assignment by another administrator.
        const latest = await fetchOperationsManagementRelationships(instanceUniqueId);
        const currentRelationships = Array.isArray(latest?.relationships) ? latest.relationships : relationships;
        const existing = currentRelationships.find((entry) =>
          entry.workflowKey === otherKey &&
          Number(entry.reviewerUserId) === Number(payload.reviewerUserId) &&
          Number(entry.managingTeamId) === Number(payload.managingTeamId) &&
          Number(entry.appliesToTeamId || entry.managedTeamId) === Number(payload.appliesToTeamId)
        );
        if (!existing) {
          await createOperationsManagementRelationship({
            ...payload, workflowKey: otherKey, canApprove: otherCanApprove,
          });
        }
      }
      handleDialogClose();
      await loadData();
    } catch (saveError) {
      setDialogError(
        saveError?.response?.data?.message ||
          saveError?.response?.data?.error ||
          (savedPrimary
            ? "The first workflow was saved, but the additional workflow failed. Retry Save to finish without duplicating the first assignment."
            : "Unable to save the workflow assignment.")
      );
      setDialogSubmitting(false);
    }
  };

  const handleStatusChange = async (relationship, isActive) => {
    try {
      await updateOperationsManagementRelationshipStatus(relationship.id, {
        instanceUniqueId,
        isActive,
      });
      await loadData();
    } catch (statusError) {
      setError(
        statusError?.response?.data?.error || "Unable to update client review assignment status."
      );
    }
  };

  const handleSettingsSave = async (updates) => {
    setSavingSettings(true);
    setError("");
    try {
      const response = await saveOperationsManagementSettings({
        instanceUniqueId,
        ...updates,
      });
      setSettings((prev) => ({ ...prev, ...(response?.settings || {}) }));
    } catch (saveError) {
      setError(
        saveError?.response?.data?.error ||
          "Unable to save operations management settings."
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSettingsToggle = async (key, value) =>
    handleSettingsSave({ [key]: value });

  const handleFallbackEmailSave = async () => {
    const normalized = String(fallbackEmailInput || "").trim().toLowerCase();
    if (!isValidEmail(normalized)) {
      setFallbackEmailError("Enter a valid fallback email address.");
      return;
    }

    setFallbackEmailError("");
    await handleSettingsSave({
      termsReviewFallbackEmail: normalized,
    });
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <Box>
          <Typography variant="h6">Operations Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign reviewers for operational workflows. Customer Review controls lead and customer conversion requests. Terms Review controls payment-term change requests submitted from sales documents.
          </Typography>
        </Box>
        {canManage ? (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => openCreateDialog()}
          >
            Add Workflow Assignment
          </Button>
        ) : null}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {canManage && !canManageFoundation && !loading ? (
        <Alert severity="info">Your permission level cannot change protected Operations workflow settings. Please contact a Super Administrator for this instance to request a change. Routine reviewer assignments remain manageable.</Alert>
      ) : null}
      {!canManage ? (
        <Alert severity="info">You can view current routing, but only administrators can change it.</Alert>
      ) : null}

      <Tabs value={operationsTab} onChange={(_, tab) => setOperationsTab(tab)}>
        <Tab label="Customer & Terms Review" />
        <Tab label="Website Lead Emails" />
      </Tabs>
      {operationsTab === 1 ? <Card variant="outlined"><CardContent><Stack spacing={2}>
        <Typography variant="h6">Website Lead Emails</Typography>
        <Typography variant="body2" color="text.secondary">Choose who receives new website inquiries. Existing customer ownership is preserved.</Typography>
        <TextField
          select
          label="Default website lead assignees"
          fullWidth
          value={websiteAssigneeIds}
          disabled={!canManage || savingSettings}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const selectedIds = new Set((selected || []).map(Number));
              const labels = websiteLeadAssigneeOptions
                .filter((user) => selectedIds.has(Number(user.userId)))
                .map(formatReviewerLabel);
              return labels.length ? labels.join(", ") : "No default assignee";
            },
          }}
          helperText="New website leads start with these Client Rep / Order Rep users. Existing customer ownership is never replaced; enabled AI assignment rules can still choose the final assignee for a brand-new lead."
          onChange={(event) => {
            const values = Array.isArray(event.target.value) ? event.target.value : [];
            setWebsiteAssigneeIds(values.map(Number).filter((value) => Number.isInteger(value) && value > 0));
            setWebsiteSaved(false);
          }}
        >
          {websiteLeadAssigneeOptions.map((user) => (
            <MenuItem key={user.userId} value={Number(user.userId)}>
              <Checkbox checked={websiteAssigneeIds.includes(Number(user.userId))} />
              {formatReviewerLabel(user)}
            </MenuItem>
          ))}
        </TextField>
        {!websiteAssigneeIds.length ? (
          <Alert severity="info">
            No default website lead assignee is selected. New leads may remain unassigned unless another assignment rule handles them.
          </Alert>
        ) : null}
        <TextField label="Notification recipients (comma-separated emails)" fullWidth multiline minRows={2}
          value={websiteRecipients} disabled={!canManage || savingSettings}
          onChange={event => { setWebsiteRecipients(event.target.value); setWebsiteSaved(false); }} />
        <FormControlLabel label="Also notify customer assigned representatives"
          control={<Checkbox checked={websiteNotifyOwners} disabled={!canManage || savingSettings}
            onChange={event => { setWebsiteNotifyOwners(event.target.checked); setWebsiteSaved(false); }} />} />
        {leadTestStatus ? <Alert severity="success">{leadTestStatus}</Alert> : null}
        <Box><Button variant="contained" disabled={!canManage || savingSettings} onClick={async () => {
          const recipients = [...new Set(websiteRecipients.split(/[,;\n]+/).map(email => email.trim().toLowerCase()).filter(Boolean))];
          setLeadTestStatus("");
          if (recipients.length > 30 || recipients.some(email => !isValidEmail(email))) {
            setError("Enter up to 30 valid email addresses."); return;
          }
          setSavingSettings(true); setError("");
          try {
            const response = await saveOperationsManagementSettings({ instanceUniqueId,
              websiteLeadRecipients: recipients,
              websiteLeadAssigneeUserIds: websiteAssigneeIds,
              websiteLeadNotifyOwners: websiteNotifyOwners });
            setSettings(prev => ({ ...prev, ...(response?.settings || {}) }));
            setWebsiteSaved(true);
          } catch (err) { setError(err?.response?.data?.error || "Unable to save website lead settings."); }
          finally { setSavingSettings(false); }
        }}>Save Changes</Button>
        <Button sx={{ ml: 1 }} variant="outlined" disabled={!canManage || savingSettings || sendingLeadTest || !websiteSaved}
          onClick={async () => {
            setSendingLeadTest(true); setError("");
            try {
              const result = await testWebsiteLeadEmail(instanceUniqueId);
              setLeadTestStatus(`Test email accepted for ${result.recipientCount} recipient(s). Check your inbox.`);
            } catch (err) { setError(err?.response?.data?.error || "Test email failed."); }
            finally { setSendingLeadTest(false); }
          }}>{sendingLeadTest ? "Sending..." : "Send Test Email"}</Button></Box>
      </Stack></CardContent></Card> : null}
      <Box sx={{ display: operationsTab === 0 ? "contents" : "none" }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Customer Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customer Review and Terms Review are separate workflows and can be enabled independently.
            </Typography>
            {canManageFoundation ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.customerReviewEnabled !== false}
                  disabled={!canManage || !canManageFoundation || savingSettings}
                  onChange={(event) =>
                    handleSettingsToggle("customerReviewEnabled", event.target.checked)
                  }
                />
              }
              label="Enable Customer Review"
            />

            ) : null}            {canManageFoundation ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.termsReviewEnabled === true}
                  disabled={!canManage || !canManageFoundation || savingSettings}
                  onChange={(event) =>
                    handleSettingsToggle("termsReviewEnabled", event.target.checked)
                  }
                />
              }
              label="Enable Terms Review"
            />

            ) : null}            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              Allows authorized sales and operations users to request different payment terms for a customer, project, or both.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
              If no reviewer assignment matches the requester's team, requests are sent to {fallbackEmail} for manual review.
            </Typography>
            {settings.termsReviewEnabled ? (
              hasConfiguredTermsReviewAssignment ? (
                <Alert severity="success">{termsReviewStatusText}</Alert>
              ) : (
                <Alert
                  severity="warning"
                  action={
                    canManage ? (
                      <Button
                        color="warning"
                        size="small"
                        onClick={() => openCreateDialog(WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW)}
                      >
                        Add Terms Reviewer
                      </Button>
                    ) : null
                  }
                >
                  {termsReviewStatusText}
                </Alert>
              )
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ pl: 4, pr: 1 }}>
              <TextField
                label="Terms Review Fallback Email"
                value={fallbackEmailInput}
                size="small"
                fullWidth
                disabled={!canManage || savingSettings}
                error={Boolean(fallbackEmailError)}
                helperText={
                  fallbackEmailError ||
                  "Used only when no active Customer Terms Review reviewer matches the requester's team."
                }
                onChange={(event) => {
                  setFallbackEmailInput(event.target.value);
                  if (fallbackEmailError) {
                    setFallbackEmailError("");
                  }
                }}
                onBlur={() => {
                  const normalized = String(fallbackEmailInput || "")
                    .trim()
                    .toLowerCase();
                  if (!normalized || isValidEmail(normalized)) {
                    setFallbackEmailError("");
                    return;
                  }
                  setFallbackEmailError("Enter a valid fallback email address.");
                }}
              />
              {canManage ? (
                <Button
                  variant="outlined"
                  onClick={handleFallbackEmailSave}
                  disabled={savingSettings}
                  sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minWidth: 120 }}
                >
                  Save Email
                </Button>
              ) : null}
            </Stack>
            {canManageFoundation ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.requireCustomerReviewBeforeTermsApproval === true}
                  disabled={!canManage || !canManageFoundation || savingSettings || settings.termsReviewEnabled !== true}
                  onChange={(event) =>
                    handleSettingsToggle(
                      "requireCustomerReviewBeforeTermsApproval",
                      event.target.checked
                    )
                  }
                />
              }
              label="Require Customer Review Before Terms Approval"
            />

            ) : null}            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pl: 4, opacity: settings.termsReviewEnabled ? 1 : 0.65 }}
            >
              When enabled, a terms request cannot be approved until the customer review workflow has been completed.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Alert severity="info">Loading operations management assignments...</Alert>
      ) : relationships.length ? (
        <Box sx={{ display: "grid", gap: 2 }}>
          {relationships.map((relationship) => (
            <Card key={relationship.id} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {relationship?.workflowLabel ||
                        getWorkflowDisplayLabel(relationship?.workflowKey)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Managing Team
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {relationship?.managingTeam?.name || "No managing team selected"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Reviewer
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {relationship?.reviewer
                        ? formatReviewerLabel(relationship.reviewer)
                        : "No reviewer selected"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Applies to Team
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {relationship?.appliesToLabel ||
                        relationship?.managedTeam?.name ||
                        "No applies-to team selected"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      Status
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {relationship?.isActive ? "Active" : "Inactive"}
                    </Typography>
                  </Box>

                  {canManage ? (
                    <Stack direction={{ xs: "row", md: "column" }} spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => openEditDialog(relationship)}
                      >
                        Edit
                      </Button>
                      {relationship?.isActive ? (
                        <Button
                          color="warning"
                          variant="outlined"
                          startIcon={<BlockRoundedIcon />}
                          onClick={() => handleStatusChange(relationship, false)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          color="success"
                          variant="outlined"
                          startIcon={<AutorenewRoundedIcon />}
                          onClick={() => handleStatusChange(relationship, true)}
                        >
                          Reactivate
                        </Button>
                      )}
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No workflow assignments are configured. Add a reviewer assignment to begin routing customer review or terms review requests.
        </Alert>
      )}

      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formValues.id
            ? `Edit ${dialogWorkflowLabel} Assignment`
            : `Add ${dialogWorkflowLabel} Assignment`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
            <TextField
              select
              label="Workflow"
              value={formValues.workflowKey}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, workflowKey: event.target.value }))
              }
              fullWidth
              size="small"
              helperText="Choose which workflow this reviewer assignment routes."
            >
              {(workflowOptions.length
                ? workflowOptions
                : [{ key: formValues.workflowKey, label: workflowLabel }]).map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox checked={addOtherWorkflow} onChange={(event) => setAddOtherWorkflow(event.target.checked)} />}
              label={`Also add ${getWorkflowDisplayLabel(formValues.workflowKey === WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW ? WORKFLOW_KEY_CLIENT_TO_CUSTOMER_REVIEW : WORKFLOW_KEY_CUSTOMER_TERMS_REVIEW)}`}
            />
            {addOtherWorkflow ? <FormControlLabel
              control={<Checkbox checked={otherCanApprove} onChange={(event) => setOtherCanApprove(event.target.checked)} />}
              label="Can approve the additional workflow (otherwise receives notifications only)"
            /> : null}
            <FormControlLabel
              control={<Checkbox checked={formValues.canApprove} onChange={(event) => setFormValues((prev) => ({ ...prev, canApprove: event.target.checked }))} />}
              label="Can approve selected workflow"
            />
            <FormControlLabel
              control={<Checkbox checked={formValues.receivesEmail} onChange={(event) => setFormValues((prev) => ({ ...prev, receivesEmail: event.target.checked }))} />}
              label="Receive email notifications"
            />
            <FormControlLabel
              control={<Checkbox checked={formValues.receivesNotifications} onChange={(event) => setFormValues((prev) => ({ ...prev, receivesNotifications: event.target.checked }))} />}
              label="Receive in-app notifications"
            />
            <TextField
              select
              label="Managing Team"
              value={formValues.managingTeamId}
              onChange={(event) =>
                setFormValues((prev) => {
                  const nextManagingTeamId = event.target.value;
                  return {
                    ...prev,
                    managingTeamId: nextManagingTeamId,
                    reviewerUserId: reconcileReviewerUserIdForManagingTeam({
                      managingTeamId: nextManagingTeamId,
                      reviewerUserId: prev.reviewerUserId,
                      teams,
                      reviewerOptions,
                    }),
                  };
                })
              }
              fullWidth
              size="small"
              required
              helperText="Select the team responsible for managing this workflow."
            >
              <MenuItem value="">
                <em>Select managing team</em>
              </MenuItem>
              {sortedTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Reviewer"
              value={formValues.reviewerUserId}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, reviewerUserId: event.target.value }))
              }
              fullWidth
              size="small"
              required
              helperText="Choose an active member of the selected managing team who should review these requests."
            >
              <MenuItem value="">
                <em>Select reviewer</em>
              </MenuItem>
              {managingTeamReviewerOptions.map((reviewer) => (
                <MenuItem key={reviewer.userId} value={reviewer.userId}>
                  {formatReviewerLabel(reviewer)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Applies to Team"
              value={formValues.appliesToTeamId}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, appliesToTeamId: event.target.value }))
              }
              fullWidth
              size="small"
              required
              helperText="Requests submitted by members of this team will be assigned to the selected reviewer."
            >
              <MenuItem value="">
                <em>Select applies-to team</em>
              </MenuItem>
              {sortedTeams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.isActive}
                  onChange={(event) =>
                    setFormValues((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
      <DialogActions>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              dialogSubmitting ||
              !normalizeId(formValues.managingTeamId) ||
              !normalizeId(formValues.appliesToTeamId) ||
              !normalizeId(formValues.reviewerUserId)
            }
          >
            {dialogSubmitting ? "Saving..." : "Save Assignment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationsManagementSettingsPanel;
